import type { ChangeEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { hideToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { AddedKWType } from "app/pages/activation/components/Topics/Modal/Flow/Advanced/types";
import { activationTracking } from "app/pages/activation/services/tracking";
import { selectToasts } from "app/selectors/uiSelectors";

import {
    MAX_PASTE_KW_LIMIT,
    REGEX_PASTE_KW,
    TIMEOUT_UPDATE_SAVED_KW,
} from "../../components/Topics/Modal/Flow/Advanced/constants";
import { getSearchResults } from "../../components/Topics/Modal/Flow/Advanced/helpers";
import { keywordModalTexts } from "../../components/Topics/Modal/Flow/Advanced/texts";
import { ActivationComponentAssets } from "../../helpers/ActivationAssets";
import { useParsing } from "../helpers/useParsing";

import useUpdater from "./useKWModalUpdater";

//TODO refactor
const useAdvancedKWModal = () => {
    const dispatch = useDispatch();

    const keywordsLimit = ActivationComponentAssets.maxAmountOfKeywords;

    const track = activationTracking.home;

    const {
        keywordsList,
        savedKeywords,
        selectedKeywords,
        removedKeywordsList,
        queryKeyword,
        title,
        keywordsGroups,
        titleError,
        showToast,
        setKeywordList,
        updateKeywordsList,
        updateSavedKeywords,
        setQueryKeyword,
        clearSelection,
        onAdd,
        onChangeTitle,
        selectKWFromSearch,
        removeKeywords,
    } = useUpdater();

    const limit = ActivationComponentAssets.maxAmountOfKeywords;

    const existingKWNames = useMemo(() => keywordsList.map((keyword) => keyword.value), [keywordsList]);

    const { parsedData, isParsing, parse } = useParsing(limit - savedKeywords?.current?.length, existingKWNames);

    const toasts = useSelector(selectToasts);

    const [isIncludedInList, setIsIncludedInList] = useState(false);

    const toastLink = useRef(null);

    const timeoutId = useRef(null);

    const deletedText =
        selectedKeywords.current.length === 1 || !selectedKeywords.current.length
            ? translate(keywordModalTexts.oneKeywordDeleted)
            : translate(keywordModalTexts.multiKeywordDeleted);

    const onSelect = (index: number) => {
        setKeywordList((keywords) => {
            const changedKeyword = keywords[index];

            changedKeyword.isSelected = !changedKeyword.isSelected;

            keywords[index] = changedKeyword;

            if (changedKeyword.isSelected) {
                selectedKeywords.current.push(changedKeyword.value);
            } else {
                selectedKeywords.current = selectedKeywords.current.filter(
                    (keyword) => keyword !== changedKeyword.value,
                );
            }

            return [...keywords];
        });
    };

    const onUndoRemove = () => {
        const keywords = removedKeywordsList.current;

        const movedBackKW = keywords.map((keyword) =>
            keyword.isSelected ? { ...keyword, isSelected: false } : keyword,
        );

        updateSavedKeywords(movedBackKW);
        dispatch(hideToast(toastLink.current));

        if (queryKeyword) {
            setKeywordList(getSearchResults(queryKeyword, movedBackKW));
            return;
        }

        setKeywordList(movedBackKW);
    };

    const onRemove = (index: number, removeAllSelected = false) => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }

        if (toasts?.length) {
            dispatch(hideToast(toastLink.current));
            removedKeywordsList.current = [];
        }

        removeKeywords(index, removeAllSelected);

        const toast = showToast(
            `${removeAllSelected ? selectedKeywords.current.length : 1} ${translate(deletedText)}`,
            translate(keywordModalTexts.undo),
            onUndoRemove,
        );

        timeoutId.current = setTimeout(() => {
            removedKeywordsList.current = [];
            timeoutId.current = null;
        }, TIMEOUT_UPDATE_SAVED_KW);

        selectedKeywords.current = [];

        toastLink.current = toast;
    };

    const onKeyPress = (e: KeyboardEvent) => {
        if (e.key === "Enter" && keywordsList.length < keywordsLimit) {
            onAdd();
        }
    };

    const onPaste = (e: ClipboardEvent) => {
        e.preventDefault();
        const itemsString = e.clipboardData.getData("Text");

        if (itemsString?.length && itemsString?.length <= MAX_PASTE_KW_LIMIT) {
            const pastedItems = itemsString.trim().split(REGEX_PASTE_KW, keywordsList.length - keywordsLimit);

            updateKeywordsList(pastedItems);
            setQueryKeyword("");

            if (!title) {
                onChangeTitle(pastedItems?.[0]);
            }

            track.trackAddNewKeyword(AddedKWType.PASTING);
        }
    };

    const onDropdownItemSelect = (id: string) => {
        const mergedKWGroups = [...keywordsGroups.ownKWList, ...keywordsGroups.sharedKWList];
        const keywordsGroup = mergedKWGroups?.find((item) => item.id === id);

        updateKeywordsList(keywordsGroup.domains);
        track.trackAddNewKeyword(AddedKWType.EXISTING);
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;

        if (toasts?.length) {
            dispatch(hideToast(toastLink.current));
            removedKeywordsList.current = [];

            clearTimeout(timeoutId.current);
        }
        if (!query) {
            setKeywordList(savedKeywords.current);
        } else {
            setKeywordList(getSearchResults(query, savedKeywords.current));
        }

        setQueryKeyword(query);
        setIsIncludedInList(existingKWNames.includes(query));
    };

    const onFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const csvFile = event?.target?.files[0];

        if (csvFile) {
            track.trackImportCSVStart();
            parse(csvFile);
        }
    };

    useEffect(() => {
        if (parsedData) {
            updateKeywordsList(parsedData);
        }
    }, [parsedData]);

    useEffect(() => {
        if (queryKeyword) {
            setIsIncludedInList(existingKWNames.includes(queryKeyword));
        }
    }, [existingKWNames]);

    return {
        keywordsList,
        queryKeyword,
        selectedKeywords,
        existingKWNames,
        isIncludedInList,
        getSearchResults,
        isParsing,
        savedKeywords,
        keywordsGroups,
        title,
        titleError,
        onFileInput,
        onDropdownItemSelect,
        clearSelection,
        onKeyPress,
        onPaste,
        onRemove,
        onSelect,
        setQueryKeyword,
        setIsIncludedInList,
        onAdd,
        onChangeTitle,
        setKeywordList,
        onChange,
        selectKWFromSearch,
    };
};

export default useAdvancedKWModal;
